/**
 * Headless presentation helpers for the cost-engine validation surfaces
 * (product brief `docs/features/cost-engine-productionisation/01_product_brief.md`).
 *
 * Cost-check state is a *validation* signal, not a *freshness* signal (doc
 * 63's "Accuracy model" — a mismatch means the cost line is failing rolling
 * validation, not that a cached price aged out).
 */

import type {
  CheckRaisedBy,
  CostCheckCellState,
  CostCheckState,
  CostHead,
  CostPairLine,
  DictionaryValidationFeedsStatus,
  DictionaryValidationMatrix,
  FixedCostOptionChip,
  RouteReasonCode,
  RouteSource,
} from "../data/cost-engine-validation-types";
import { formatCurrencyGbp } from "./formatters";

export type ValidationTone = "success" | "warning" | "error" | "neutral";

/** The rolling-validation page's four roll-up views — a URL tab key with no
 *  wire representation, so it stays hand-defined here rather than in
 *  `data/cost-engine-validation-types.ts` (which aliases wire shapes only). */
export type RollingValidationView =
  "item" | "cost-line" | "cost-centre" | "product-type";

/** "On Tharstern's route" / "On our route" — the check-history feed cell's
 *  third secondary line (cost-engine-productionisation spec 07 §8.5), keyed
 *  on the backend's `RouteSource` enum rather than a ternary with two
 *  literals inline. Rendered on both the cost-line page's checks table and
 *  the cost-pair page's Check history section. */
export const ROUTE_SOURCE_LINES: Record<RouteSource, string> = {
  tharstern_recorded: "On Tharstern's route",
  ours_forced: "On our route",
};

export function formatRouteSourceLine(routeSource: RouteSource): string {
  return ROUTE_SOURCE_LINES[routeSource];
}

/** "Cost check: Matches / Small differences / Mismatch / Not yet checked" —
 *  the agreed vocabulary for whether our engine's cost agrees with
 *  Tharstern's recorded cost. */
export const COST_CHECK_STATES: Record<
  CostCheckState,
  { label: string; tone: ValidationTone }
> = {
  green: { label: "Matches", tone: "success" },
  amber: { label: "Small differences", tone: "warning" },
  red: { label: "Mismatch", tone: "error" },
  grey: { label: "Not yet checked", tone: "neutral" },
};

export function costCheckStateLabel(state: CostCheckState): string {
  return COST_CHECK_STATES[state].label;
}

export function costCheckStateTone(state: CostCheckState): ValidationTone {
  return COST_CHECK_STATES[state].tone;
}

/** Tharstern's own cost-head labels — the grouping used on the cost-pair
 *  page (review decision, 02_glossary_and_live_costing.md). */
export const COST_HEAD_LABELS: Record<CostHead, string> = {
  printing: "Printing",
  finishing: "Finishing",
  paper: "Paper",
  other_material: "Other material",
  origination: "Origination",
  outwork: "Outwork",
  carriage: "Carriage",
  order_expense: "Order expense",
};

/** Display order for cost-head groups on the pair page — matches the order
 *  agreed with the product owner. */
export const COST_HEAD_ORDER: CostHead[] = [
  "printing",
  "finishing",
  "paper",
  "other_material",
  "origination",
  "outwork",
  "carriage",
  "order_expense",
];

export function costHeadLabel(head: CostHead): string {
  return COST_HEAD_LABELS[head];
}

/** "Brochure Cover & Text (PT1)" — the Tharstern product type's own name
 *  with its code, never a bare "PT1"/"PT 1" (coordinator review round 4).
 *  Shared by the dictionary index and the Live costing items table so the
 *  two never disagree on the label. `productTypeId` is `null` for a
 *  supplier-only item (#71) — no in-house route exists to name. */
export function productTypeLabel(
  productTypeId: number | null,
  name: string,
): string {
  if (productTypeId == null) return "No in-house route";
  return `${name} (PT${productTypeId})`;
}

/**
 * The rolling-validation header card's single summary line. Never hides an
 * over-budget set — when the set exceeds the weekly mint budget, says so
 * explicitly with how many prices will wait for a later week, per the
 * product owner's review decision ("...rather than hiding it"). The week is
 * checked in paced hourly slices (Ben, 24 Sep 2026), but "will wait" still
 * counts the set beyond the WEEKLY budget: a price beyond one slice's cap is
 * picked up by a later slice the same week, so it is not waiting in any
 * sense a reader cares about.
 */
export function formatRollingValidationSetSummary(
  status: DictionaryValidationFeedsStatus,
): string {
  const { rollingSetSize } = status;
  const budget = status.rollingValidation.mintBudgetThisWeek ?? 0;
  if (budget > 0 && rollingSetSize > budget) {
    const waiting = rollingSetSize - budget;
    return `${rollingSetSize} prices in the set, ${budget} checked per week in hourly slices — ${waiting} will wait for a later week`;
  }
  return `${rollingSetSize} ${rollingSetSize === 1 ? "price" : "prices"} checked weekly`;
}

// ── Cost-line board / drill-in money formatting ─────────────────────────────
// Shared between DictionaryAdminPage's "by cost line" board and the
// cost-line drill-in page — both render the same Difference (total) /
// Largest over / Largest under figures.

/** `Intl.NumberFormat` renders a negative amount with the ASCII hyphen-minus
 *  (U+002D); the glossary calls for the Unicode minus sign (U+2212)
 *  ("−£61.00", not "-£61.00"). Swap only a leading hyphen (the sign). */
function toProperMinus(text: string): string {
  return text.replace(/^-/, "−");
}

/** "−£61.00 (9 checks)" — the sum of Δ over every check that differs, with
 *  how many differed. */
export function formatCostLineDifferenceTotal(
  pence: number,
  count: number,
): string {
  return `${toProperMinus(formatCurrencyGbp(pence / 100, 2))} (${count} ${count === 1 ? "check" : "checks"})`;
}

/** "+£4.10 (2)" / "−£12.30 (7)" — the single largest Δ in that direction,
 *  with how many checks fell that way. Currency formatting supplies a sign
 *  for a negative amount natively, but not "+" for a positive one — added
 *  explicitly so "over" reads unambiguously as a positive delta at a
 *  glance. */
export function formatCostLineLargestDelta(
  pence: number,
  count: number,
): string {
  const sign = pence > 0 ? "+" : "";
  return `${sign}${toProperMinus(formatCurrencyGbp(pence / 100, 2))} (${count})`;
}

// ── Validation matrix navigation (cost-pair quantity/variant pickers) ──────
// Walks the validation matrix's own cell shape — every cell here is
// navigable (there is always something to show: computed on the fly, never
// "no cost row yet"). Prev/next single-step adjacency was replaced by full
// pickers (build decision, 12 Sep — see `quantitiesInVariant`/
// `variantsAtQuantity` below).

/** Every quantity costed for one variant, ascending — the pair page's
 *  quantity picker (build decision, 12 Sep: prev/next chevrons became
 *  dropdowns so a print manager can jump straight to a quantity rather
 *  than stepping through every one between). */
export function quantitiesInVariant(
  matrix: DictionaryValidationMatrix,
  variantId: string,
  fixedCostOptions: readonly FixedCostOptionChip[] = [],
): number[] {
  const key = fixedCostOptionsKey(fixedCostOptions);
  const row = matrix.rows.find(
    (r) =>
      r.variantId === variantId &&
      fixedCostOptionsKey(r.fixedCostOptions ?? []) === key,
  );
  if (!row) return [];
  return [...row.cells.map((c) => c.quantity)].sort((a, b) => a - b);
}

/** Every variant costed at one quantity, in the matrix's own row order —
 *  the pair page's variant picker (same build decision as
 *  `quantitiesInVariant`). */
export function variantsAtQuantity(
  matrix: DictionaryValidationMatrix,
  quantity: number,
): { variantId: string; displayLabel: string }[] {
  // Plain rows only: a fixed-cost corner row repeats its variant id, and the
  // variant picker chooses a variant, not a corner of one.
  return matrix.rows
    .filter((r) => (r.fixedCostOptions ?? []).length === 0)
    .filter((r) => r.cells.some((c) => c.quantity === quantity))
    .map((r) => ({ variantId: r.variantId, displayLabel: r.displayLabel }));
}

// ── Fixed-cost corners ──────────────────────────────────────────────────
// A price may switch catalogue fixed-cost options on over its variant (spec
// 03 rule (d)); these helpers give the one spelling of such a selection for
// React keys, labels, request bodies and URLs.

/** A stable identity for a selection — "" for the variant as generated. */
export function fixedCostOptionsKey(
  options: readonly FixedCostOptionChip[],
): string {
  return options
    .map((o) => `${o.fieldKey}=${o.optionCode}`)
    .sort()
    .join(",");
}

/** "PDF proof · File copy" — the corner row / pair heading label. */
export function fixedCostOptionsLabel(
  options: readonly FixedCostOptionChip[],
): string {
  return [...options]
    .sort((a, b) => a.fieldKey.localeCompare(b.fieldKey))
    .map((o) => o.label)
    .join(" · ");
}

/** The inverse of `fixedCostOptionSearchValues` (routing/routes.ts). Labels are not in the
 *  URL, so each chip's label is its code until the pair response supplies
 *  the catalogue's own. Malformed values are dropped. */
export function fixedCostOptionsFromSearchValues(
  values: readonly string[],
): FixedCostOptionChip[] {
  return values.flatMap((value) => {
    const at = value.indexOf(":");
    if (at <= 0 || at === value.length - 1) return [];
    const fieldKey = value.slice(0, at);
    const optionCode = value.slice(at + 1);
    return [{ fieldKey, optionCode, label: optionCode }];
  });
}

// ── Cost-pair banner sentence ───────────────────────────────────────────
// "Driven by 5351 · Ink — differs on 9 of 30 checks, all under Tharstern" —
// rule-based, no adjectives (build decision, `04_handover_2026-09-12.md`):
// which cost line drives the pair's state, how many of its checks differ,
// and whether the differing checks all run one direction.

/** "all under Tharstern" (ours below on every differing check) / "all over
 *  Tharstern" (ours above on every one) / "2 over and 7 under Tharstern"
 *  (a genuine mix — never collapsed to the word "mixed") — the direction
 *  phrase for the banner sentence, from the cost line's own largest-over/
 *  largest-under counts (the same figures the by-cost-line board shows),
 *  not a second re-derivation from raw check rows. */
function directionPhrase(
  largestOverCount: number,
  largestUnderCount: number,
): string {
  if (largestUnderCount > 0 && largestOverCount === 0)
    return "all under Tharstern";
  if (largestOverCount > 0 && largestUnderCount === 0)
    return "all over Tharstern";
  return `${largestOverCount} over and ${largestUnderCount} under Tharstern`;
}

/**
 * The pair page's banner sentence, keyed off the pair's own cell state
 * (coordinator review round 5 — a "Matches" pair with no specific driving
 * cost line was falling through to "Not yet checked against Tharstern.",
 * which is only correct for a genuinely never-checked ("grey") pair):
 *
 * - grey → "Not yet checked against Tharstern."
 * - a specific cost line is driving (has actually been checked) → the
 *   existing "Driven by X — ..." sentence, from that cost line's own
 *   aggregate figures (`CostLineIndexEntry.checkCount`/`missCount`/
 *   `moneySummary`) — the same numbers the by-cost-line board shows,
 *   never re-derived from a possibly-paged check-history listing.
 * - green with no specific driving line (most matching pairs — nothing
 *   about this exact price has ever differed enough to name a cause) →
 *   "Matches Tharstern on all N checks in the last M weeks", N/M from
 *   this pair's own check history and the check-settings window.
 */
export function formatCostPairBannerSentence(params: {
  cellState: CostCheckCellState;
  costLineLabel: string | null;
  differingCheckCount: number;
  totalCheckCount: number;
  largestOverCount: number;
  largestUnderCount: number;
  pairCheckCount: number;
  checkWindowWeeks: number;
}): string {
  const { cellState, costLineLabel, differingCheckCount, totalCheckCount } =
    params;
  if (cellState === "grey") {
    return "Not yet checked against Tharstern.";
  }
  if (costLineLabel && totalCheckCount > 0) {
    if (differingCheckCount === 0) {
      return `Driven by ${costLineLabel} — matches Tharstern on all ${totalCheckCount} checks.`;
    }
    return (
      `Driven by ${costLineLabel} — differs on ${differingCheckCount} of ` +
      `${totalCheckCount} checks, ${directionPhrase(params.largestOverCount, params.largestUnderCount)}.`
    );
  }
  if (cellState === "green") {
    const { pairCheckCount, checkWindowWeeks } = params;
    return (
      `Matches Tharstern on all ${pairCheckCount} ${pairCheckCount === 1 ? "check" : "checks"} ` +
      `in the last ${checkWindowWeeks} ${checkWindowWeeks === 1 ? "week" : "weeks"}.`
    );
  }
  // amber/red always name a specific driving cost line in practice; this
  // is an honest fallback for the (currently unmodelled) case where they
  // don't, rather than a silently wrong "matches" claim.
  return "Not yet checked against Tharstern.";
}

// ── Route-reason templates ──────────────────────────────────────────────
// The engine's closed set of route-selection reasons (`RouteReasonCode`) —
// every cost-pair group-header "why" traces to exactly one of these, never
// free text authored independent of a code (build note,
// `04_handover_2026-09-12.md` "Every 'why' must trace to a reason code").

export interface RouteReasonParams {
  /** Required for `cheapest_suitable`. */
  suitableCount?: number;
  /** Required for `site_rule`. */
  site?: string;
  /** `cheapest_suitable`/`only_suitable` only — which noun the wording
   *  uses. Defaults to "press"; pass "cost_centre" for anything that
   *  isn't a press (a guillotine, folder, plate room, laminator…) —
   *  "Only suitable press" on a guillotine is simply wrong (coordinator
   *  review round 5). */
  kind?: "press" | "cost_centre";
  /** OPTIONAL for `spec_input_unresolved` — what could not be resolved, as
   *  the engine described it (e.g. "part 0: stock code 'SILK350' is not in
   *  the product catalogue"). The engine authors this fragment because only
   *  it knows which input failed and what the configuration asked for; the
   *  sentence around it is rendered here.
   *
   *  Not populated by any production path TODAY, and the JSDoc used to claim
   *  it was required, which was simply untrue. `reason_params` exists only on
   *  a RoutedCostCentre — i.e. only on a decision that ROUTED — whereas
   *  `spec_input_unresolved` is only ever produced by a refusal, which
   *  carries `message` and no params at all. The detail is therefore
   *  reachable (it is on `RouteDecisionSnapshot.message`) but not yet wired:
   *  the seam that would wire it is the views_module renderer. Until then the
   *  fallback sentence below is what renders, and it is graceful. */
  detail?: string;
}

function kindNoun(kind: RouteReasonParams["kind"]): string {
  return kind === "cost_centre" ? "cost centre" : "press";
}

function kindNounPlural(kind: RouteReasonParams["kind"]): string {
  return kind === "cost_centre" ? "cost centres" : "presses";
}

const ROUTE_REASON_TEMPLATES: Record<
  RouteReasonCode,
  (p: RouteReasonParams) => string
> = {
  // "Cheapest of 1 suitable presses" reads as a defect, not a fact — a
  // count of exactly one collapses to the same wording as `only_suitable`.
  cheapest_suitable: (p) =>
    (p.suitableCount ?? 0) <= 1
      ? `Cheapest suitable ${kindNoun(p.kind)}`
      : `Cheapest of ${p.suitableCount} suitable ${kindNounPlural(p.kind)}`,
  only_suitable: (p) => `Only suitable ${kindNoun(p.kind)}`,
  step_map_default: () => "Step-map default",
  site_rule: (p) => `Site rule: binder at ${p.site ?? "the finishing site"}`,
  indigo_option: () => "Indigo option on this item",
  skipped_not_yet_checked: () => "Skipped: not yet used in any price check",
  cross_site_tie: (p) => `Cross-site cost tie — ${p.site ?? "Exeter"} wins`,
  no_press_on_feasible_site: () => "No press on a feasible site",
  // Not a routing reason at all: the engine could not resolve an input it
  // needs out of the catalogue configuration. Deliberately worded to send the
  // reader to the ITEM, not to the presses -- which is exactly what surfacing
  // this as `no_press_on_feasible_site` used to do.
  spec_input_unresolved: (p) =>
    p.detail
      ? `An input the engine needs could not be resolved from the catalogue configuration: ${p.detail}`
      : "An input the engine needs could not be resolved from the catalogue configuration",
};

/** Plain-English label for a `RouteReasonCode`, independent of the
 *  routing-policy version suffix the pair page appends itself. */
export function formatRouteReasonText(
  code: RouteReasonCode,
  params: RouteReasonParams = {},
): string {
  return ROUTE_REASON_TEMPLATES[code](params);
}

// ── Cost-pair line grouping (by cost head, then by cost centre) ─────────
// The "why" is per cost centre, not per cost head (coordinator review round
// 2): two cost centres under one head — a binder and a guillotine, both
// "finishing" — can have different reasons, so the head itself never
// carries a single reason. Group by head first (the display grouping,
// subtotalled), then by cost centre within each head (the reason grouping).

export function groupCostPairLinesByCostHead(
  lines: CostPairLine[],
): Array<{ head: CostHead; lines: CostPairLine[] }> {
  return COST_HEAD_ORDER.map((head) => ({
    head,
    lines: lines.filter((l) => l.costHead === head),
  })).filter((group) => group.lines.length > 0);
}

/** Groups a cost-head group's own lines by cost centre, preserving each
 *  cost centre's first-seen order — a press cost centre with several
 *  operations (make-ready, run, wash…) stays one sub-group. Lines with no
 *  cost centre (paper, other material, order expense) group under `null`. */
export function groupCostPairLinesByCostCentre(
  lines: CostPairLine[],
): Array<{ costCentreId: number | null; lines: CostPairLine[] }> {
  const order: Array<number | null> = [];
  const byCostCentre = new Map<number | null, CostPairLine[]>();
  for (const line of lines) {
    if (!byCostCentre.has(line.costCentreId)) {
      order.push(line.costCentreId);
      byCostCentre.set(line.costCentreId, []);
    }
    byCostCentre.get(line.costCentreId)!.push(line);
  }
  return order.map((costCentreId) => ({
    costCentreId,
    lines: byCostCentre.get(costCentreId)!,
  }));
}

// ── Catalogue item's compact "cost drilldown" line ───────────────────────
// "Costed on 5351 (cover) and 5830 (text), cheapest suitable presses ·
// policy v3" / "Costed on 803 (all parts), cheapest suitable press · policy
// v3" — the catalogue item page's one-line echo of the pair page's own
// per-cost-centre reasons (one concept, one name: `costDrilldownLine`
// everywhere, never "route provenance").

export interface CostDrilldownPress {
  costCentreId: number;
  /** From that press's `partSpecLabel`, e.g. "Cover" — lowercased in the
   *  sentence. */
  partName: string;
  code: RouteReasonCode;
}

function combinedReasonPhrase(
  codes: RouteReasonCode[],
  plural: boolean,
): string {
  const unique = [...new Set(codes)];
  if (unique.length === 1) {
    switch (unique[0]) {
      case "cheapest_suitable":
      case "only_suitable":
        return plural ? "cheapest suitable presses" : "cheapest suitable press";
      case "indigo_option":
        return "Indigo option on this item";
      case "site_rule":
        return plural ? "site rules" : "site rule";
      case "step_map_default":
        return plural ? "step-map defaults" : "step-map default";
      case "skipped_not_yet_checked":
        return "not yet used in any price check";
      case "cross_site_tie":
        return plural ? "cross-site cost ties" : "cross-site cost tie";
      case "no_press_on_feasible_site":
        return "no press on a feasible site";
      case "spec_input_unresolved":
        return "an unresolved input on this item";
    }
  }
  return "chosen by routing policy";
}

export function formatCostDrilldownLine(
  presses: CostDrilldownPress[],
  policyVersion: number,
): string | null {
  if (presses.length === 0) return null;
  const location =
    presses.length === 1
      ? `${presses[0].costCentreId} (all parts)`
      : presses
          .map((p) => `${p.costCentreId} (${p.partName.toLowerCase()})`)
          .join(" and ");
  const phrase = combinedReasonPhrase(
    presses.map((p) => p.code),
    presses.length > 1,
  );
  return `Costed on ${location}, ${phrase} · policy v${policyVersion}`;
}

// ── "Raised by" — which checkout produced a "Checked at estimate" check ──
// Send estimate and Order now both make exactly one Tharstern EstRequest
// submission; Order now is that same call followed by setting the estimate
// to ORDERED (product-owner decision, 12 Sep — this is why the feed is
// "Checked at estimate", not "at order"). `raisedBy` is provenance for the
// check, never a second check source alongside "at_estimate"/
// "rolling_validation".

/** "Raised by Order now · ordered" / "Raised by Send estimate · not yet
 *  ordered" — the check-history feed's secondary line on the pair page and
 *  the cost-line page. `null` for a rolling-validation check, which has no
 *  checkout to name. */
export function formatCheckRaisedBy(
  raisedBy: CheckRaisedBy | null,
  orderedAt: string | null,
): string | null {
  if (raisedBy === null) return null;
  const checkoutLabel =
    raisedBy === "order_now" ? "Order now" : "Send estimate";
  return `Raised by ${checkoutLabel} · ${orderedAt ? "ordered" : "not yet ordered"}`;
}
